/**
 * UserManagementCard Component
 *
 * Administrative interface card for user account management dashboard display.
 * Shows user statistics and navigation to detailed user management functions.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody, CardFooter, Button, Chip, Spinner } from '@enfomentis/ui';
import { Link as RouterLink } from 'react-router-dom';
import { Users, UserCheck, TrendingUp } from 'lucide-react';
import { userService } from '@/services/UserService';
import type { UserInvitationStats } from '@/types/supabase/User';

/**
 * Props for UserManagementCard component
 */
export interface UserManagementCardProps {
  /** Optional title override */
  title?: string;
  /** Whether to show detailed metrics */
  showDetails?: boolean;
}

/**
 * User Management Card Component
 *
 * Features:
 * - Real-time user statistics from UserService
 * - Invitation funnel metrics and completion rates
 * - Responsive design matching AdminDashboard patterns
 * - Loading states and error handling
 * - Navigation integration for detailed user management
 * - Consistent styling with existing admin interface
 * - Contractor integration hooks for enhanced analytics
 *
 * Usage:
 * ```tsx
 * <UserManagementCard />
 * <UserManagementCard title="Custom Title" showDetails={false} />
 * ```
 */
export const UserManagementCard: React.FC<UserManagementCardProps> = ({
  title = "User Management",
  showDetails = true
}) => {
  // State management
  const [stats, setStats] = useState<UserInvitationStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Load user statistics
  const loadUserStats = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const userStats = await userService.getInvitationStats();
      setStats(userStats);
    } catch (err) {
      console.error('Failed to load user statistics:', err);
      setError('Failed to load user metrics');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadUserStats();
  }, []);

  // Status chip component for user metrics
  const MetricChip: React.FC<{
    count: number;
    label: string;
    color: 'primary' | 'success' | 'warning' | 'danger' | 'secondary' | 'default';
  }> = ({ count, label, color }) => (
    <div className="text-center">
      <p className={`text-lg font-bold text-${color}`}>{count}</p>
      <p className="text-xs text-text-secondary">{label}</p>
    </div>
  );

  // Get completion rate display color
  const getCompletionRateColor = (rate: number): 'success' | 'warning' | 'danger' => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'danger';
  };

  return (
    <Card shadow="sm" className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary bg-opacity-10">
            <Users className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h4 className="font-semibold text-text-primary">{title}</h4>
            <div className="flex gap-2 flex-wrap">
              {loading ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  <span className="text-xs text-text-secondary">Loading...</span>
                </div>
              ) : error ? (
                <Chip size="sm" color="danger" variant="flat">
                  Error Loading
                </Chip>
              ) : stats ? (
                <>
                  <Chip size="sm" color="primary" variant="flat">
                    {stats.totalUsers} Total Users
                  </Chip>
                  {stats.pendingInvitations > 0 && (
                    <Chip size="sm" color="warning" variant="flat">
                      {stats.pendingInvitations} Pending
                    </Chip>
                  )}
                </>
              ) : null}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardBody className="pt-0 space-y-4">
        <p className="text-sm text-text-secondary mb-3">
          Manage user accounts, invitations, and onboarding progress
        </p>

        {loading && (
          <div className="flex items-center justify-center py-4">
            <Spinner color="primary" size="sm" />
            <span className="ml-2 text-sm text-text-secondary">Loading user statistics...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-4">
            <p className="text-sm text-danger mb-2">{error}</p>
            <Button
              size="sm"
              variant="flat"
              color="primary"
              onPress={loadUserStats}
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && stats && showDetails && (
          <div className="space-y-4">
            {/* User Status Metrics */}
            <div className="grid grid-cols-3 gap-2 p-3 bg-default-50 dark:bg-default-900/20 rounded-lg">
              <MetricChip
                count={stats.activeUsers}
                label="Active"
                color="success"
              />
              <MetricChip
                count={stats.pendingInvitations}
                label="Invited"
                color="warning"
              />
              <MetricChip
                count={stats.disabledUsers}
                label="Disabled"
                color="danger"
              />
            </div>

            {/* Onboarding Progress */}
            {stats.totalUsers > 0 && (
              <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp size={14} className="text-primary" />
                    <span className="text-sm font-medium text-primary">
                      Onboarding Progress
                    </span>
                  </div>
                  <Chip
                    size="sm"
                    color={getCompletionRateColor(stats.onboardingCompletionRate)}
                    variant="flat"
                  >
                    {stats.onboardingCompletionRate}%
                  </Chip>
                </div>
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span>{stats.completedOnboarding} completed</span>
                  <span>{stats.totalUsers - stats.completedOnboarding} remaining</span>
                </div>
              </div>
            )}

          </div>
        )}
      </CardBody>

      <CardFooter>
        <RouterLink to="/admin/users" className="w-full">
          <Button
            color="primary"
            variant="flat"
            size="sm"
            className="w-full"
            startContent={<UserCheck size={16} />}
          >
            Manage Users
          </Button>
        </RouterLink>
      </CardFooter>
    </Card>
  );
};